import { Button } from '@wellesley/ui'
import { MINUTE } from 'constants/date'
import { useDispatch } from 'react-redux'
import { FC, useEffect, useState } from 'react'
import { Post, usePostApiV1PollsByPollIdVoteMutation } from '@wellesley/openapi'
import pluralize from 'helpers/pluralize'

import { feedsSlice } from '../../../feedsSlice'

import PollRow from './PollRow'
import getPollTimeLabel from './helpers/getPollTimeLabel'

type FeedPollSectionProps = {
  feed: Post
}

const getTimeDiff = (expiresAt: string) =>
  new Date(expiresAt).getTime() - new Date().getTime()

const FeedPollSection: FC<FeedPollSectionProps> = ({ feed }) => {
  const dispatch = useDispatch()

  const pollData = feed.poll
  const ownVotes = pollData?.ownVotes || []
  const isMultiplePoll = pollData?.multiple || false

  const [selectedVote, setSelectedVote] = useState<number[]>(ownVotes)

  const [makeVote] = usePostApiV1PollsByPollIdVoteMutation()

  const updatePost = (index?: number) => {
    if (!feed.poll) return

    const feedPoll = feed.poll
    const isSingleVote = index !== undefined
    const ownVotes = isSingleVote ? [index] : selectedVote

    dispatch(
      feedsSlice.actions.updatePost({
        ...feed,
        poll: {
          ...feedPoll,
          options: feedPoll.options.map((option, index) => ({
            ...option,
            votesCount:
              (option?.votesCount || 0) + (ownVotes.includes(index) ? 1 : 0),
          })),
          ownVotes,
          votersCount: (feedPoll.votersCount || 0) + ownVotes.length,
        },
      }),
    )
  }

  const handleVote = async (index?: number) => {
    if (feed.poll) {
      await makeVote({
        pollId: feed.poll.id,
        body: index !== undefined ? [index] : selectedVote,
      }).unwrap()

      updatePost(index)
    }
  }

  const totalVotes =
    pollData?.options.reduce((acc, poll) => (acc += poll.votesCount || 0), 0) ||
    0

  const isVotedPoll = !!pollData?.ownVotes?.length
  const isPollEnded =
    new Date(pollData?.expiresAt || '').getTime() < new Date().getTime()
  const canShowResult = isVotedPoll || isPollEnded

  const [timeDiff, setTimeDiff] = useState(
    getTimeDiff(pollData?.expiresAt || ''),
  )

  const isNonExpiredPoll = !pollData?.expiresAt

  useEffect(() => {
    setTimeDiff(getTimeDiff(pollData?.expiresAt || '')) // case when edit poll duration

    const interval = setInterval(() => {
      setTimeDiff(getTimeDiff(pollData?.expiresAt || ''))
    }, MINUTE)

    return () => clearInterval(interval)
  }, [pollData?.expiresAt])

  const timeLabel = getPollTimeLabel(timeDiff)

  return (
    <div className="flex flex-col">
      <div className="gap-xxs flex flex-col">
        {pollData?.options.map((option, index) => (
          <PollRow
            key={index}
            option={option}
            isSingleChoice={!isMultiplePoll}
            result={
              totalVotes > 0 ? (100 / totalVotes) * (option.votesCount || 0) : 0
            }
            isUserVoted={!!pollData?.ownVotes?.includes(index)}
            isSelected={selectedVote.includes(index)}
            canShowResult={canShowResult}
            onVote={() => {
              if (isMultiplePoll) {
                setSelectedVote((prev) =>
                  prev.includes(index)
                    ? prev.filter((v) => v !== index)
                    : [...prev, index],
                )
              } else {
                // For single vote, send request to vote
                void handleVote(index)
              }
            }}
          />
        ))}
      </div>
      <div className="mt-[14px] flex items-center justify-between max-md:flex-col">
        <p className="text-base text-gray-500">
          <span data-testid="votes-label">
            {pluralize('vote', totalVotes, true)}&nbsp;
          </span>
          {!isNonExpiredPoll && (
            <span data-testid="time-label">
              {isPollEnded ? '· Closed' : `· ${timeLabel} left`}
            </span>
          )}
          {!isVotedPoll && (
            <span data-testid="anonymous-label">· Anonymously</span>
          )}
        </p>
        {pollData?.multiple && !pollData?.ownVotes?.length && (
          <Button
            disabled={selectedVote.length === 0}
            variant="primary"
            className="max-md:mt-s max-md:w-full"
            onClick={() => void handleVote()}
          >
            Vote
          </Button>
        )}
      </div>
    </div>
  )
}

export default FeedPollSection
